import { CircleCheck, CircleX, FlaskConical } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toValueLabel } from "~/utils/form-utils";
import { extractFilename, SAMPLE_PATHS, testPattern } from "~/utils/pattern-utils";
import { Collapse, Input, Select, Tag, Text } from "~/components/ui";

interface DualPatternTesterProps {
  pathPattern?: string;
  filenamePattern?: string;
  /** Schema regex_flags (IGNORECASE/MULTILINE/DOTALL) so the preview matches server behavior. */
  regexFlags?: string[] | null;
}

export function DualPatternTester({ pathPattern, filenamePattern, regexFlags }: DualPatternTesterProps) {
  const { t } = useTranslation("admin");
  const [isOpen, setIsOpen] = useState(false);
  const [testInput, setTestInput] = useState(SAMPLE_PATHS[0]);

  const hasAnyPattern = pathPattern || filenamePattern;

  const results = useMemo(() => {
    if (!testInput) return { path: null, filename: null };

    const filename = extractFilename(testInput);

    return {
      path: pathPattern ? testPattern(pathPattern, testInput, regexFlags) : null,
      filename: filenamePattern ? testPattern(filenamePattern, filename, regexFlags) : null,
    };
  }, [pathPattern, filenamePattern, testInput, regexFlags]);

  if (!hasAnyPattern) return null;

  return (
    <Collapse
      size="small"
      ghost
      activeKey={isOpen ? ["tester"] : []}
      onChange={(keys) => setIsOpen(keys.includes("tester"))}
      className="mt-2 -mx-2"
      items={[
        {
          key: "tester",
          label: (
            <Text tone="muted" className="text-xs">
              <FlaskConical size={14} className="mr-1" />
              {t("schemaAdmin.pattern.testBothPatterns")}
            </Text>
          ),
          children: (
            <div className="space-y-3">
              <div>
                <Text tone="muted" className="text-xs block mb-1">
                  {t("schemaAdmin.pattern.testPath")}
                </Text>
                <Input
                  value={testInput}
                  onChange={(e) => setTestInput(e.target.value)}
                  placeholder="/path/to/file.pdf"
                  className="font-mono text-sm"
                />
              </div>

              <Select
                size="small"
                placeholder={t("schemaAdmin.pattern.samplePaths")}
                options={SAMPLE_PATHS.map(toValueLabel)}
                onChange={setTestInput}
                className="w-full"
                value={null}
              />

              <div className="grid grid-cols-2 gap-4 pt-2 border-t border-noa">
                {pathPattern && (
                  <div>
                    <Text strong className="text-xs block mb-1">
                      {t("schemaAdmin.field.pathPattern")}
                    </Text>
                    <code className="text-xs bg-noa-inset px-1 rounded block mb-1 truncate" title={pathPattern}>
                      {pathPattern}
                    </code>
                    {results.path?.error ? (
                      <Tag color="error" icon={<CircleX size={14} />} className="text-xs">
                        {results.path.error}
                      </Tag>
                    ) : results.path?.success ? (
                      <Tag color="success" icon={<CircleCheck size={14} />} className="text-xs">
                        {t("schemaAdmin.pattern.captured")}: <code>{results.path.captured}</code>
                      </Tag>
                    ) : (
                      <Tag color="default" className="text-xs">
                        {t("schemaAdmin.pattern.noMatch")}
                      </Tag>
                    )}
                  </div>
                )}

                {filenamePattern && (
                  <div>
                    <Text strong className="text-xs block mb-1">
                      {t("schemaAdmin.field.filenamePattern")}
                    </Text>
                    <code className="text-xs bg-noa-inset px-1 rounded block mb-1 truncate" title={filenamePattern}>
                      {filenamePattern}
                    </code>
                    <Text tone="muted" className="text-xs block mb-1">
                      {t("schemaAdmin.pattern.testedOn")}: <code>{extractFilename(testInput)}</code>
                    </Text>
                    {results.filename?.error ? (
                      <Tag color="error" icon={<CircleX size={14} />} className="text-xs">
                        {results.filename.error}
                      </Tag>
                    ) : results.filename?.success ? (
                      <Tag color="success" icon={<CircleCheck size={14} />} className="text-xs">
                        {t("schemaAdmin.pattern.captured")}: <code>{results.filename.captured}</code>
                      </Tag>
                    ) : (
                      <Tag color="default" className="text-xs">
                        {t("schemaAdmin.pattern.noMatch")}
                      </Tag>
                    )}
                  </div>
                )}
              </div>
            </div>
          ),
        },
      ]}
    />
  );
}
